import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Spinner } from '@mt/ui';
import { useAuth } from './AuthProvider.tsx';
import { useMe } from '../lib/queries.ts';
import { ErrorState, FullScreenMessage } from '../components/common.tsx';

export function Splash({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-paper" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3 text-muted">
        <Spinner className="size-7 text-primary" />
        <span className="text-sm font-medium">{label}…</span>
      </div>
    </div>
  );
}

/** Signed-in routes. Remembers where the user was going. */
export function RequireAuth() {
  const { session, restoring, expired, signedOut } = useAuth();
  const location = useLocation();
  if (restoring) return <Splash />;
  // After a deliberate sign-out, don't offer to return to the page the user was on.
  if (!session && signedOut) return <Navigate to="/signin" replace />;
  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?next=${next}${expired ? '&expired=1' : ''}`} replace />;
  }
  return <Outlet />;
}

/** Only same-app paths are allowed as post-sign-in destinations. */
export function safeNext(next: string | null): string | null {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : null;
}

/** Auth pages redirect away when already signed in (to ?next= when present). */
export function PublicOnly() {
  const { session, restoring } = useAuth();
  const location = useLocation();
  if (restoring) return <Splash />;
  if (session) return <Navigate to={safeNext(new URLSearchParams(location.search).get('next')) ?? '/'} replace />;
  return <Outlet />;
}

/** Requires a completed onboarding. */
export function RequireOnboarded() {
  const me = useMe();
  if (me.isPending) return <Splash />;
  if (me.isError)
    return (
      <FullScreenMessage title="We couldn’t load your account">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} />
      </FullScreenMessage>
    );
  if (!me.data.profile.onboardedAt) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

/** Guest practice: signed-in learners use their saved practice instead. */
export function GuestOnly() {
  const { session, restoring } = useAuth();
  if (restoring) return <Splash />;
  if (session) return <Navigate to="/practice" replace />;
  return <Outlet />;
}

/** "/" → the front page when signed out, else the workspace the user last used. */
export function RootRedirect({ signedOut }: { signedOut: ReactNode }) {
  const { session, restoring } = useAuth();
  const me = useMe(Boolean(session));
  if (restoring || (session && me.isPending)) return <Splash />;
  if (!session) return <>{signedOut}</>;
  if (me.isError) return <Navigate to="/home" replace />;
  if (!me.data?.profile.onboardedAt) return <Navigate to="/onboarding" replace />;
  return <Navigate to={me.data.profile.activeWorkspace === 'teacher' ? '/teach' : '/home'} replace />;
}
